import { useCallback } from 'react';
import { OrganisationAnalytics } from 'shared/analytics/organisation';
import { InvitationData } from 'shared/types/api/user';
import { ROLE } from 'shared/types/OrganisationUser';

import { useOrganisationsContext } from '@/context/OrganisationsContext';
import { FirebaseAnalytics } from '@/services/firebase/analytics';
import { OrganisationsInvitationsAPI } from '@/services/networking/invitations';
import { OrganisationsAPI } from '@/services/networking/organisations';

type UpdateMemberRoleParams = {
    memberId: string;
    organisationId: string;
    userRole: ROLE;
};

export function useOrganisationsLogic() {
    const {
        fetchUserOrganisationsData,
        setCurrentOrganisation,
        userOrganisations,
    } = useOrganisationsContext();

    const openInvitation = useCallback(
        async (
            flow: 'v1' | 'v2',
            invitation: Pick<
                InvitationData,
                'id' | 'user_id' | 'organisation_id'
            >,
        ) => {
            const isInvitationFlowV2 = flow === 'v2';

            if (isInvitationFlowV2) {
                const data = await OrganisationsInvitationsAPI.openInvitationV2(
                    {
                        invitation_id: invitation.id,
                        user_id: invitation.user_id,
                        organisation_id: invitation.organisation_id,
                    },
                );

                return data.payload.id;
            } else {
                const data = await OrganisationsInvitationsAPI.openInvitationV1(
                    {
                        invitation_id: invitation.id,
                        user_id: invitation.user_id,
                        organisation_id: invitation.organisation_id,
                    },
                );

                const { id } = data.payload.invitation;

                return id;
            }
        },
        [],
    );

    const approveInvitation = useCallback(async (invitationId: string) => {
        await OrganisationsInvitationsAPI.updateOwnerInvitationStatus({
            user_invitation_id: invitationId,
            status: 'approved',
        });
        OrganisationAnalytics.logTeamInvitationApproved(
            FirebaseAnalytics.logEvent,
            { invitationId },
        );
    }, []);

    const rejectInvitation = useCallback(async (invitationId: string) => {
        await OrganisationsInvitationsAPI.updateOwnerInvitationStatus({
            user_invitation_id: invitationId,
            status: 'declined',
        });
    }, []);

    const acceptInvitation = useCallback(async (invitationId: string) => {
        await OrganisationsInvitationsAPI.updateUserInvitationStatus({
            user_invitation_id: invitationId,
            status: 'accepted',
        });
    }, []);

    const acceptInvitationV2 = useCallback(async (invitationId: string) => {
        await OrganisationsInvitationsAPI.acceptInvitation({
            invitation_id: invitationId,
        });
    }, []);

    const declineInvitation = useCallback(async (invitationId: string) => {
        await OrganisationsInvitationsAPI.updateUserInvitationStatus({
            user_invitation_id: invitationId,
            status: 'rejected',
        });
    }, []);

    const declineInvitationV2 = useCallback(async (invitationId: string) => {
        await OrganisationsInvitationsAPI.cancelInvitation({
            invitation_id: invitationId,
        });
    }, []);

    const dismissInvitation = useCallback(async (invitationId: string) => {
        await OrganisationsInvitationsAPI.dismissInvitation({
            user_invitation_id: invitationId,
        });
    }, []);

    const updateMemberRole = useCallback(
        async ({
            memberId,
            organisationId,
            userRole,
        }: UpdateMemberRoleParams) => {
            await OrganisationsAPI.updateMemberRole({
                memberId,
                organisationId,
                userRole,
            });
        },
        [],
    );

    const removeMember = useCallback(
        async (memberId: string, organisationId: string) => {
            await OrganisationsInvitationsAPI.removeMember({
                member_id: memberId,
                organisation_id: organisationId,
            });
        },
        [],
    );

    const changeOrganisation = useCallback(
        (organisationId: string) => {
            const organisation = userOrganisations?.find(
                (org) => org.id === organisationId,
            );

            if (organisation) {
                setCurrentOrganisation(organisation);
            }
        },
        [setCurrentOrganisation, userOrganisations],
    );

    const joinOrganisation = useCallback(
        async (invitationId: string, flow: 'v1' | 'v2') => {
            const joinInvitationAsync =
                flow === 'v2' ? acceptInvitationV2 : acceptInvitation;

            await joinInvitationAsync(invitationId);

            await fetchUserOrganisationsData();
        },
        [acceptInvitation, acceptInvitationV2, fetchUserOrganisationsData],
    );

    return {
        acceptInvitation,
        approveInvitation,
        changeOrganisation,
        declineInvitation,
        dismissInvitation,
        joinOrganisation,
        openInvitation,
        rejectInvitation,
        removeMember,
        updateMemberRole,
        acceptInvitationV2,
        declineInvitationV2,
    };
}
